import React, { useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  styled,
  Alert,
  CircularProgress,
} from '@mui/material';
import { CloudArrowUp, X } from '@phosphor-icons/react';
import theme from '../../theme';
import ReusableModal from '../common/Modal';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { uploadOutboundFileApi } from '../../utils/apis';

const UploadIconBox = styled(Box)({
  backgroundColor: 'custom.sidebarIconBg',
  padding: '8px',
  borderRadius: '6px',
  marginBottom: '8px',
});

const UploadInstructions = styled(Typography)({
  textAlign: 'center',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 400,
  color: 'text.secondary',
});

const UploadButton = styled(Button)({
  color: 'primary.main',
  textTransform: 'none',
  padding: 0,
  minWidth: 'auto',
});

interface OutboundFileUploadModalProps {
  agentId: string;
  open: boolean;
  onClose: () => void;
}

const OutboundFileUploadModal: React.FC<OutboundFileUploadModalProps> = ({
  agentId,
  open,
  onClose,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [alert, setAlert] = React.useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const queryClient = useQueryClient();

  const handleFileUpload = (event: React.MouseEvent) => {
    event.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const { mutate: uploadFile, isPending } = useMutation({
    mutationFn: uploadOutboundFileApi,
    onSuccess: async () => {
      // Refetch queries to get updated file list
      await queryClient.refetchQueries({ queryKey: ['outbound-files'] });
      setSelectedFile(null);

      setAlert({
        type: 'success',
        message: 'File uploaded successfully!',
      });
      setTimeout(() => {
        setAlert(null);
      }, 2000);
      onClose();
    },
    onError: () => {
      setAlert({
        type: 'error',
        message: 'Failed to upload file. Please try again.',
      });
    },
  });

  const handleSubmitFile = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (selectedFile) {
      uploadFile({ file: selectedFile, agentId });
    }
  };

  const handleRemoveFile = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <ReusableModal
      open={open}
      onClose={onClose}
      title="Upload Files"
      subtitle="Please upload a file with phone numbers for outbound calls"
      width={500}
    >
      {alert && (
        <Alert
          severity={alert.type}
          sx={{ mb: 2 }}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}
      <Box sx={{ mt: 2 }}>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".csv,.xlsx"
          style={{ display: 'none' }}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px dashed #DCE0E7',
            borderRadius: '8px',
            p: 3,
            width: '100%',
            cursor: 'pointer',
          }}
          onClick={handleFileUpload}
        >
          {!selectedFile ? (
            <>
              <UploadIconBox>
                <CloudArrowUp size={24} color={theme.palette.lightGray.text} />
              </UploadIconBox>
              <UploadInstructions variant="body2">
                Ensure it's in a supported format (csv/xlsx)
              </UploadInstructions>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}
              >
                <UploadInstructions variant="body2">
                  Drop files here or
                </UploadInstructions>
                <UploadButton onClick={handleFileUpload}>
                  Browse from device
                </UploadButton>
              </Box>
            </>
          ) : (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2,
                  width: '100%',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="body1">
                  Selected file: {selectedFile.name}
                </Typography>
                <Box
                  onClick={handleRemoveFile}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover': {
                      opacity: 0.7,
                    },
                  }}
                >
                  <X size={20} color={theme.palette.error.main} />
                </Box>
              </Box>
              <Button
                variant="contained"
                onClick={handleSubmitFile}
                disabled={isPending}
                sx={{ textTransform: 'none' }}
                startIcon={
                  isPending ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                {isPending ? 'Uploading...' : 'Upload File'}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </ReusableModal>
  );
};

export default OutboundFileUploadModal;
